import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { productsAPI, usersAPI, favoritesAPI, followsAPI, formatApiError, resolveImageUrl } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PageShell from '../components/PageShell';
import { MagnifyingGlass, Storefront, Heart, ArrowRight, UserPlus, UserMinus } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { stripHtmlToText } from '../utils/text';
import { profileLink } from '../utils/profile';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";
const CATEGORIES = ['All', 'Art', 'Auto Services', 'Books', 'Clothing', 'Coaching', 'Consulting', 'Design', 'Development', 'Electronics', 'Food', 'Health & Beauty', 'Jewelry', 'Marketing', 'Music', 'Musical Instruments', 'Photography', 'Teaching', 'Toys & Games', 'Transportation', 'Video', 'Writing', 'Other'];

export default function ExplorePage() {
  const { user, isAuthenticated } = useAuth();
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const category = selectedCategory === 'All' ? null : selectedCategory;
      const [productsRes] = await Promise.all([
        productsAPI.getAll(category)
      ]);
      setProducts(productsRes.data);
      
      if (isAuthenticated) {
        const [favRes, followRes] = await Promise.all([
          favoritesAPI.list(),
          followsAPI.getFollowing()
        ]);
        setFavorites(favRes.data);
        setFollowing(followRes.data.map(u => u.user_id));
      }
    } catch (err) {
      console.error('Failed to load marketplace data:', err);
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, isAuthenticated]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setUsers([]);
      return;
    }
    
    try {
      const response = await usersAPI.search(query);
      setUsers(response.data.filter(u => u.user_id !== user?.user_id));
    } catch (err) {
      console.error('User search failed:', err);
    }
  };

  const handleFavoriteProduct = async (productId) => {
    if (!isAuthenticated) {
      toast.error('Please login to add favorites');
      return;
    }
    
    try {
      const existing = favorites.find(f => f.type === 'product' && f.target_id === productId);
      if (existing) {
        await favoritesAPI.remove(existing.favorite_id);
        setFavorites(favorites.filter(f => f.favorite_id !== existing.favorite_id));
        toast.success('Removed from favorites');
      } else {
        const response = await favoritesAPI.favoriteProduct(productId);
        setFavorites([...favorites, { ...response.data, type: 'product', target_id: productId }]);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleFollowUser = async (userId) => {
    if (!isAuthenticated) {
      toast.error('Please login to follow users');
      return;
    }
    
    try {
      if (following.includes(userId)) {
        await followsAPI.unfollow(userId);
        setFollowing(following.filter(id => id !== userId));
        toast.success('Unfollowed');
      } else {
        await followsAPI.follow(userId);
        setFollowing([...following, userId]);
        toast.success('Now following!');
      }
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const isProductFavorited = (productId) => favorites.some(f => f.type === 'product' && f.target_id === productId);
  const isUserFollowed = (userId) => following.includes(userId);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <PageShell testId="explore-page" title={{ line1: 'Marketplace', line2: 'Discover & buy from creators' }}>
      <div className="explore-content">
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Explore Marketplace
          </h1>
          <p className="text-[#999999] mt-2">Discover products, services, and vendors</p>
        </div>

        {/* Search */}
        <div className="max-w-xl mx-auto mb-8">
          <div className="relative">
            <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#999999]" />
            <Input
              type="text"
              placeholder="Search users by $username, name, or email..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="rounded-sm bg-[#1A1A1A] border-[#333333] text-white placeholder:text-[#666666] pl-12 h-12 focus:ring-2 focus:ring-[#22C55E] focus:ring-offset-1 focus:ring-offset-[#111111]"
              data-testid="search-input"
            />
          </div>
          
          {/* User Search Results */}
          {users.length > 0 && (
            <Card className="mt-2 bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-lg">
              <CardContent className="p-0">
                {users.map((u) => (
                  <div
                    key={u.user_id}
                    className="flex items-center justify-between p-4 border-b border-[#333333] last:border-0 hover:bg-[#222222]"
                  >
                    <Link to={profileLink(u)} className="flex items-center gap-3 flex-1">
                      {u.avatar_url ? (
                        <img src={u.avatar_url} alt={u.name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-[#22C55E]/20 rounded-full flex items-center justify-center text-[#22C55E] font-semibold">
                          {u.name?.[0]?.toUpperCase() || 'U'}
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-white">{u.name}</p>
                        <p className="text-sm text-[#999999]">
                          {u.username ? `$${u.username}` : u.email}
                        </p>
                      </div>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleFollowUser(u.user_id)}
                      className={isUserFollowed(u.user_id) ? 'text-[#22C55E]' : 'text-[#999999] hover:text-white'}
                      data-testid={`follow-user-${u.user_id}`}
                    >
                      {isUserFollowed(u.user_id) ? <UserMinus className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Category Filter */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Products & Services
          </h2>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-40 rounded-sm bg-[#1A1A1A] border-[#333333] text-white" data-testid="category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-sm bg-[#1A1A1A] border-[#333333]">
              {CATEGORIES.map((cat) => (
                <SelectItem key={cat} value={cat} className="text-white hover:bg-[#333333]">{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12 text-[#999999]">Loading...</div>
        ) : products.length === 0 ? (
          <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none">
            <CardContent className="p-12 text-center">
              <Storefront className="w-16 h-16 mx-auto mb-4 text-[#333333]" />
              <p className="text-[#999999]">No products found in this category</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {products.map((product) => (
              <Link
                key={product.product_id}
                to={`/product/${product.product_id}`}
                className="block"
                data-testid={`product-card-link-${product.product_id}`}
              >
              <Card 
                className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none hover:-translate-y-1 hover:border-[#22C55E] transition-all duration-200 overflow-hidden cursor-pointer"
                data-testid={`product-card-${product.product_id}`}
              >
                {product.image_url ? (
                  <div className="aspect-square bg-[#1A1A1A] overflow-hidden relative">
                    <img 
                      src={resolveImageUrl(product.image_url)} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; if (e.target.nextElementSibling) e.target.nextElementSibling.style.display = 'flex'; }}
                    />
                    <div className="absolute inset-0 items-center justify-center" style={{ display: 'none' }}>
                      <Storefront className="w-10 h-10 text-[#333333]" />
                    </div>
                  </div>
                ) : (
                  <div className="aspect-square bg-[#1A1A1A] flex items-center justify-center">
                    <Storefront className="w-10 h-10 text-[#333333]" />
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs bg-[#333333] px-2 py-1 rounded-sm text-[#999999]">
                        {product.category}
                      </span>
                      {product.listing_type && product.listing_type !== 'product' && (() => {
                        const badges = {
                          service: { label: 'Service', color: '#22C55E' },
                          subscription: { label: 'Subscription', color: '#22C55E' },
                          crowdfund: { label: 'Crowdfund', color: '#22C55E' },
                          divispend: { label: 'DiviSpend', color: '#22C55E' },
                          offer: { label: 'Make Offer', color: '#22C55E' },
                          tip_jar: { label: 'Tip Jar', color: '#22C55E' },
                          digital_download: { label: 'Download', color: '#22C55E' },
                          booking: { label: 'Booking', color: '#22C55E' },
                        };
                        const b = badges[product.listing_type];
                        return b ? <span className="text-[10px] px-2 py-1 rounded-sm font-semibold" style={{ backgroundColor: `${b.color}15`, color: b.color }}>{b.label}</span> : null;
                      })()}
                      {!product.listing_type && product.is_crowdfund && (
                        <span className="text-[10px] bg-[#22C55E]/10 px-2 py-1 rounded-sm text-[#22C55E] font-semibold">Crowdfund</span>
                      )}
                      {!product.listing_type && product.investment_enabled && !product.is_crowdfund && (
                        <span className="text-[10px] bg-[#22C55E]/10 px-2 py-1 rounded-sm text-[#22C55E] font-semibold">DiviSpend</span>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleFavoriteProduct(product.product_id); }}
                      className={`p-1 h-auto ${isProductFavorited(product.product_id) ? 'text-red-500' : 'text-[#999999]'}`}
                      data-testid={`favorite-product-${product.product_id}`}
                    >
                      <Heart className="w-5 h-5" weight={isProductFavorited(product.product_id) ? "fill" : "regular"} />
                    </Button>
                  </div>
                  
                  <h3 className="font-bold text-white mb-1 line-clamp-1" style={{ fontFamily: 'Cabinet Grotesk' }}>
                    {product.name}
                  </h3>
                  <p className="text-sm text-[#999999] mb-3 line-clamp-2">
                    {stripHtmlToText(product.description)}
                  </p>
                  
                  {/* Seller Info */}
                  {product.seller && (
                    <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = product.seller.username ? `/store/${product.seller.username}` : '#'; }} className="flex items-center gap-2 mb-3 group cursor-pointer">
                      {product.seller.avatar_url ? (
                        <img src={product.seller.avatar_url} alt={product.seller.name} className="w-6 h-6 rounded-full object-cover" data-testid={`seller-avatar-${product.product_id}`} />
                      ) : (
                        <div className="w-6 h-6 bg-[#22C55E]/20 rounded-full flex items-center justify-center text-[#22C55E] font-semibold text-xs" data-testid={`seller-avatar-${product.product_id}`}>
                          {product.seller.name?.[0]?.toUpperCase() || 'U'}
                        </div>
                      )}
                      <span className="text-xs text-[#999999] group-hover:text-[#22C55E] transition-colors" data-testid={`seller-name-${product.product_id}`}>
                        {product.seller.name}{product.seller.username ? ` ($${product.seller.username})` : ''}
                      </span>
                    </span>
                  )}
                  
                  <div className="flex items-center justify-between">
                    {product.is_crowdfund ? (
                      <div className="flex-1 mr-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-[#22C55E] font-semibold">${(product.amount_raised || 0).toLocaleString()} raised</span>
                          <span className="text-[10px] text-[#666666]">of ${(product.funding_goal || 0).toLocaleString()}</span>
                        </div>
                        <div className="w-full h-1.5 bg-[#333333] rounded-full overflow-hidden">
                          <div className="h-full bg-[#22C55E] rounded-full transition-all" style={{ width: `${Math.min(100, ((product.amount_raised || 0) / (product.funding_goal || 1)) * 100)}%` }} />
                        </div>
                        <span className="text-[10px] text-[#999999] mt-0.5 block">{product.backer_count || 0} backer{(product.backer_count || 0) !== 1 ? 's' : ''}</span>
                      </div>
                    ) : (
                      <p className="text-xl font-bold text-[#22C55E]" style={{ fontFamily: 'Cabinet Grotesk' }}>
                        {formatCurrency(product.price)}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </PageShell>
  );
}
